import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScrollText, Loader2, LogOut, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import RichText from "./RichText";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { shortDate } from "../lib/format";

/**
 * Blocks the product until the member has accepted the current terms and privacy
 * policy.
 *
 * Deliberate constraints, because a consent dialog that traps people is worse than
 * no dialog at all:
 *   - the full text is *in* the dialog, not behind a link. A dialog you have to
 *     navigate away from to read is one most people accept blind, and one a lawyer
 *     would take apart.
 *   - signing out is always available.
 *   - the legal pages themselves stay reachable, because the gate lives in the app
 *     shell and not over the whole site.
 *   - one dialog per document, in order, each recorded separately — "I agree to all
 *     of it" is a single record that proves less.
 *
 * A changed version of the terms comes with four weeks' notice (backend/legal.py).
 * During that time it is *not blocking*: a banner announces it and the member may
 * read and accept it early, or leave it. From the effective date it blocks again,
 * and the member may accept or object; an objection lets them carry on under the
 * version they accepted.
 */
export default function LegalGate() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [bannerHidden, setBannerHidden] = useState(false);
  const lang = (i18n.language || "de").startsWith("en") ? "en" : "de";

  const load = useCallback(() => {
    if (!user) { setPending([]); return; }
    api.legalPending()
      .then((r) => setPending(r.data.pending || []))
      .catch(() => {})          // never let this break the app for a member
      .finally(() => setLoaded(true));
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const blocking = pending.filter((p) => p.blocking !== false);
  const notice = pending.find((p) => p.blocking === false);
  // A blocking document always comes first; an announced change only when opened.
  const doc = blocking[0] || (reviewing ? notice : null);
  // Every document gets its own tick. Carrying the checkbox over from the previous
  // one would mean the second acceptance was never actually given.
  useEffect(() => { setAgreed(false); }, [doc?.key, doc?.version]);

  if (!user || !loaded) return null;

  if (!doc) {
    if (!notice || (bannerHidden && !notice.termination_effective_at)) return null;
    // Someone given notice after objecting sees, every time, when their account ends —
    // that banner cannot be dismissed.
    const ending = notice.termination_effective_at;
    return (
      <div className="fixed inset-x-0 top-0 z-50 p-2 sm:p-3" data-testid="legal-notice-banner">
        <div className={`mx-auto max-w-3xl rounded-xl px-4 py-2.5 flex items-center gap-3 text-sm shadow-sm border ${
          ending ? "bg-rose-50 border-rose-200 text-rose-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
          <ScrollText size={16} className="shrink-0" />
          <span className="flex-1 min-w-0">
            {ending
              ? t("legalGate.terminationBanner", { date: shortDate(ending) })
              : t("legalGate.noticeBanner", { doc: t(`legal.${notice.key}`), date: shortDate(notice.effective_at) })}
          </span>
          <button className="font-medium underline shrink-0" onClick={() => setReviewing(true)}
                  data-testid="legal-notice-review">
            {t("legalGate.review")}
          </button>
          {!ending && (
            <button className="shrink-0 text-amber-700" onClick={() => setBannerHidden(true)}
                    aria-label={t("legalGate.later")}>
              <X size={15} />
            </button>
          )}
        </div>
      </div>
    );
  }

  const accept = async () => {
    setBusy(true);
    try {
      await api.legalAccept({ key: doc.key, version: doc.version });
      setPending((p) => p.filter((x) => !(x.key === doc.key && x.version === doc.version)));
      setReviewing(false);
    } catch (e) {
      // 409 means it was republished while the dialog was open; reloading shows the
      // new text rather than recording consent to something already superseded.
      toast.error(e.response?.data?.detail || t("dialogs.failed"));
      load();
    } finally { setBusy(false); }
  };

  const object = async () => {
    if (!window.confirm(t("legalGate.objectConfirm"))) return;
    setBusy(true);
    try {
      await api.legalObject({ key: doc.key, version: doc.version });
      setPending((p) => p.filter((x) => !(x.key === doc.key && x.version === doc.version)));
      toast.success(t("legalGate.objected"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dialogs.failed"));
      load();
    } finally { setBusy(false); }
  };

  const html = doc[`html_${lang}`] || doc.html_de || doc.html_en || "";
  const announced = doc.blocking === false;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4"
         role="dialog" aria-modal="true" data-testid="legal-gate">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl flex flex-col max-h-[92vh]">
        <div className="p-5 sm:p-6 border-b border-slate-100 shrink-0">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl bg-brand-50 text-brand-600 shrink-0"><ScrollText size={18} /></div>
            <div className="min-w-0">
              <h2 className="font-heading font-semibold tracking-tight text-lg">
                {t(doc.is_update ? "legalGate.updatedTitle" : "legalGate.title",
                   { doc: t(`legal.${doc.key}`) })}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {t("legalGate.version", { version: doc.version, date: shortDate(doc.published_at) })}
                {blocking.length > 1 && ` · ${t("legalGate.remaining", { count: blocking.length })}`}
              </p>
              {announced && !doc.termination_effective_at && (
                <p className="text-sm text-amber-700 mt-1" data-testid="legal-gate-effective">
                  {t("legalGate.effectiveOn", { date: shortDate(doc.effective_at) })}
                </p>
              )}
              {doc.termination_effective_at && (
                <p className="text-sm text-rose-700 mt-1" data-testid="legal-gate-termination">
                  {t("legalGate.terminationBanner", { date: shortDate(doc.termination_effective_at) })}
                </p>
              )}
              {doc.change_summary && (
                <p className="text-sm text-slate-600 mt-1.5" data-testid="legal-gate-summary">
                  <strong>{t("legal.whatChanges")}</strong> {doc.change_summary}
                </p>
              )}
            </div>
            {announced && (
              <button className="ml-auto text-slate-400 hover:text-slate-700" onClick={() => setReviewing(false)}
                      aria-label={t("legalGate.later")}>
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4">
          <RichText html={html} />
        </div>

        <div className="p-5 sm:p-6 border-t border-slate-100 shrink-0 space-y-3">
          <label className="flex items-start gap-2.5 text-sm cursor-pointer">
            <input type="checkbox" className="mt-0.5" data-testid="legal-gate-checkbox"
                   checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span className="text-slate-700">
              {/* The privacy policy is information (DSGVO Art. 13), not something a
                  member agrees to, so it is acknowledged rather than accepted. */}
              {t(doc.key === "privacy" ? "legalGate.confirmRead" : "legalGate.confirm",
                 { doc: t(`legal.${doc.key}`) })}
            </span>
          </label>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {announced ? (
              <button className="btn-ghost text-sm" onClick={() => setReviewing(false)} data-testid="legal-gate-later">
                {t("legalGate.later")}
              </button>
            ) : doc.can_object ? (
              <button className="btn-ghost text-sm" onClick={object} disabled={busy} data-testid="legal-gate-object">
                {t("legalGate.object")}
              </button>
            ) : (
              <button className="btn-ghost text-sm" onClick={logout} data-testid="legal-gate-logout">
                <LogOut size={15} /> {t("legalGate.signOut")}
              </button>
            )}
            <div className="flex items-center gap-3">
              <Link to={`/${doc.key}`} className="text-xs text-slate-400 hover:text-brand-600">
                {t("legalGate.openPage")}
              </Link>
              <button className="btn-primary" onClick={accept} disabled={!agreed || busy}
                      data-testid="legal-gate-accept">
                {busy ? <Loader2 size={15} className="animate-spin" /> : null}
                {t(doc.key === "privacy" ? "legalGate.continue" : "legalGate.accept")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
